<template>
  <label
    class="jps-switch"
    :class="{
      'left-label': labelPosition === 'left',
      'is-hina': isHina,
      'is-jps': isJps,
    }"
    tabindex="0"
    role="checkbox"
    @keydown.space.prevent="keyToggle"
  >
    <input
      tabindex="-1"
      type="checkbox"
      v-model="switchValue"
      class="u-screen-reader-only"
    />
    <span class="slider-parent">
      <span class="jps-switch-slider" />
    </span>
    <span class="jps-switch-label">
      <slot />
    </span>
  </label>
</template>
<script lang="ts" setup>
const { isHina, isJps } = useServiceType()

interface Props {
  modelValue?: boolean
  disabled?: boolean
  labelPosition?: string
}
const props = withDefaults(defineProps<Props>(), {
  disabled: false
})

const emits = defineEmits(['update:modelValue'])

const switchValue = computed({
  get: () => props.modelValue,
  set: v => {
    emits('update:modelValue', v)
  },
})

const keyToggle = event => {
  if (props.disabled) {
    return
  }
  switchValue.value = !switchValue.value
}
</script>

<style lang="scss" scoped>
@use '@/assets/styles/scss/vue' as *;

.jps-switch {
  --gap: 4px;
  --height: 26px;
  --width: calc(var(--height) * 2 - var(--gap) * 2);
  --button-height: calc(var(--height) - var(--gap) * 2);

  &.is-jps {
    --active-color: #{$jps-key-blue};
  }
  &.is-hina {
    --active-color: #{$hina-accent-green-accessible};
  }

  position: relative;
  display: inline-flex;
  align-items: center;
  height: var(--height);
  gap: 0.5rem;

  &:disabled {
    pointer-events: none;
    opacity: 0.6;
  }

  .slider-parent {
    position: relative;
    width: var(--width);
    height: var(--height);

    .jps-switch-slider {
      position: absolute;
      cursor: pointer;
      width: var(--width);
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      background-color: $jps-gray-300;
      transition: 250ms;
      border-radius: 999px;
      &:before {
        position: absolute;
        content: '';
        height: var(--button-height);
        width: var(--button-height);
        left: var(--gap);
        bottom: var(--gap);
        background-color: white;
        transition: 250ms;
        border-radius: 999px;
      }
    }
  }

  input:checked + .slider-parent .jps-switch-slider {
    background-color: var(--active-color);
  }

  input:focus + .slider-parent .jps-switch-slider {
    box-shadow: 0 0 1px var(--active-color);
  }

  &.is-red input:checked + .slider-parent .jps-switch-slider {
    background-color: $jps-key-red;
  }

  &.is-red input:focus + .slider-parent .jps-switch-slider {
    box-shadow: 0 0 1px $jps-key-red;
  }

  input:checked + .slider-parent .jps-switch-slider:before {
    transform: translateX(var(--button-height));
  }

  .jps-switch-label {
    padding-left: 4px;
    padding-right: 4px;
    width: max-content;
    cursor: pointer;
    font-size: 0.875rem;
    font-weight: normal;
    user-select: none;
  }

  &.left-label {
    flex-direction: row-reverse;
  }
}
</style>
